/**
 * AQ-Cloud2 brand lockup — the official "AutoQuotes from Revalize" logo used
 * across the header, auth shell, footer, and any place we need the primary
 * brand mark.
 *
 * Renders the raster logo asset (`src/assets/autoquotes-logo.png`), which is the
 * complete lockup (AQ tile + wordmark). Because the raster is atomic, `compact`
 * simply scales it down rather than dropping the wordmark.
 *
 * The asset is **cropped to the ink bounding box and has a transparent
 * background**, so the height below is the height of the mark itself — there is
 * no whitespace padding baked into the image and no white plate to clash with
 * the surrounding surface. It is derived from the report source of truth
 * (`backend/assets/reports/autoquotes-logo.png`) by
 * `frontend/scripts/buildBrandLockup.mjs`; regenerate with:
 *   node frontend/scripts/buildBrandLockup.mjs
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import logoSrc from '@/assets/autoquotes-logo.png'

export type BrandLockupSize = 'sm' | 'md' | 'lg'

interface BrandLockupProps {
  size?: BrandLockupSize
  /** Render a smaller variant. Useful on narrow viewports / the footer. */
  compact?: boolean
  /** href for the home link. Defaults to "/". Pass `null` to render a non-link span. */
  to?: string | null
  className?: string
}

/**
 * Rendered mark height (px) per size. Width is derived from the asset ratio.
 *
 * `md` is tuned for the 56px (`h-14`) app header: a 30px mark leaves 13px of
 * clearance above and below. `lg` is for standalone brand moments (splash /
 * error shells) where the lockup is the focal point.
 */
const HEIGHTS: Record<BrandLockupSize, number> = {
  sm: 22,
  md: 30,
  lg: 44,
}

/** Intrinsic aspect ratio of the cropped asset (526x129). */
const ASPECT = 526 / 129

const ALT = 'AutoQuotes from Revalize'

export const BrandLockup = ({
  size = 'md',
  compact = false,
  to = '/',
  className,
}: BrandLockupProps): JSX.Element => {
  const height = compact ? Math.round(HEIGHTS[size] * 0.85) : HEIGHTS[size]

  const img = (
    <img
      src={logoSrc}
      alt={ALT}
      // Both dimensions are declared so the browser reserves the correct box
      // before the raster loads (no layout shift in the header).
      width={Math.round(height * ASPECT)}
      height={height}
      style={{ height }}
      className="w-auto shrink-0 select-none"
      draggable={false}
      decoding="async"
    />
  )

  const classes = cn('inline-flex items-center', className)

  if (to === null) {
    return (
      <span className={classes} aria-label={ALT}>
        {img}
      </span>
    )
  }

  return (
    <Link
      to={to}
      className={cn(
        classes,
        // The lockup is the "home" affordance — give it a visible focus ring and
        // a subtle hover cue without shifting the header layout.
        'rounded-sm transition-opacity duration-fast hover:opacity-80',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      )}
      aria-label={`${ALT} — home`}
    >
      {img}
    </Link>
  )
}
